import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Chip,
  Tooltip,
  useTheme,
  TableSortLabel
} from '@mui/material';
import { formatCurrency, formatNumber, formatQuantity } from './formatters';

interface ProductData {
  cod_item: string;
  descricao_produto: string;
  credito_icms: number;
  debito_icms: number;
  total_icms: number;
  total_transacoes: number;
  quantidade_total: number;
  valor_total_item?: number;
  valor_total_compra?: number;
  valor_total_venda?: number;
}

interface ProductsSectionProps {
  produtos: ProductData[];
  title?: string;
  showPagination?: boolean;
  maxHeight?: string;
}

type SortableColumns = 'cod_item' | 'descricao_produto' | 'credito_icms' | 'debito_icms' | 'total_icms' | 'total_transacoes' | 'quantidade_total' | 'valor_total_item' | 'valor_total_compra' | 'valor_total_venda';

interface SortConfig {
  key: SortableColumns;
  direction: 'asc' | 'desc';
}

const DrillDownProductsSection: React.FC<ProductsSectionProps> = ({
  produtos = [],
  title = "Principais Produtos",
  showPagination = true,
  maxHeight = "600px"
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'total_icms', direction: 'desc' });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column: SortableColumns) => {
    const isAsc = sortConfig.key === column && sortConfig.direction === 'asc';
    setSortConfig({ key: column, direction: isAsc ? 'desc' : 'asc' });
  };

  const sortedProducts = React.useMemo(() => {
    if (!produtos || produtos.length === 0) return [];
    
    return [...produtos].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' 
          ? aValue - bValue
          : bValue - aValue;
      }
      
      return 0;
    });
  }, [produtos, sortConfig]);

  const paginatedProducts = showPagination
    ? sortedProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedProducts;

  const getSaldoColor = (credito: number, debito: number) => {
    const saldo = debito - credito;
    if (saldo > 0) return theme.palette.success.main;
    if (saldo < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  const getSaldoChipColor = (credito: number, debito: number) => {
    const saldo = debito - credito;
    if (saldo > 0) return 'success';
    if (saldo < 0) return 'error';
    return 'default';
  };

  if (!produtos || produtos.length === 0) {
    return (
      <Grid item xs={12}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Nenhum produto encontrado
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom >
              {title}
      
            <Chip 
            sx={{ marginRight: '10px', marginLeft: '10px' }}
              label={`${formatNumber(produtos.length)} produtos`}
              color="primary"
              variant="outlined"
              size="small"
            />
                  </Typography>
          </Box>
          
          <TableContainer sx={{ maxHeight: maxHeight }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'cod_item'}
                      direction={sortConfig.key === 'cod_item' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('cod_item')}
                    >
                      Código
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortConfig.key === 'descricao_produto'}
                      direction={sortConfig.key === 'descricao_produto' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('descricao_produto')}
                    >
                      Descrição
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === 'valor_total_compra'}
                      direction={sortConfig.key === 'valor_total_compra' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('valor_total_compra')}
                    >
                      Valor Compras
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === 'valor_total_venda'}
                      direction={sortConfig.key === 'valor_total_venda' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('valor_total_venda')}
                    >
                      Valor Vendas
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === 'total_transacoes'}
                      direction={sortConfig.key === 'total_transacoes' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('total_transacoes')}
                    >
                      Transações
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === 'quantidade_total'}
                      direction={sortConfig.key === 'quantidade_total' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('quantidade_total')}
                    >
                      Quantidade
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === 'credito_icms'}
                      direction={sortConfig.key === 'credito_icms' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('credito_icms')}
                    >
                      Crédito ICMS
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={sortConfig.key === 'debito_icms'}
                      direction={sortConfig.key === 'debito_icms' ? sortConfig.direction : 'asc'}
                      onClick={() => handleSort('debito_icms')}
                    >
                      Débito ICMS
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">
                    Saldo ICMS
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProducts.map((produto, index) => {
                  const saldo = produto.credito_icms - produto.debito_icms;
                  const precoMedio = produto.quantidade_total > 0 
                    ? (produto.valor_total_item ?? ((produto.valor_total_compra ?? 0) + (produto.valor_total_venda ?? 0))) / produto.quantidade_total 
                    : 0;
                  
                  return (
                    <TableRow key={`${produto.cod_item}-${index}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {produto.cod_item}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={produto.descricao_produto} arrow>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              maxWidth: '200px', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {produto.descricao_produto}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main">
                          {formatCurrency(produto.valor_total_compra || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="warning.main">
                          {formatCurrency(produto.valor_total_venda || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                        {formatQuantity(produto.quantidade_total)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatNumber(produto.total_transacoes)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="info.main">
                          {formatCurrency(produto.credito_icms)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="warning.main">
                          {formatCurrency(produto.debito_icms)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={formatCurrency(saldo)}
                          color={getSaldoChipColor(produto.debito_icms, produto.credito_icms)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          
          {showPagination && produtos.length > rowsPerPage && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={produtos.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Linhas por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          )}
        </CardContent>
      </Card>
    </Grid>
  );
};

export default DrillDownProductsSection; 